import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { VscInfo } from "react-icons/vsc";

import ConfirmationModal from "../../../../../../components/Dialogs/ConfirmationModal";
import { useAppState } from "../../../../../../context/StateContext";
import useActionCustomer from "../../../../../../hooks/useActionCustomer";
import { Customer, CustomerIds } from "../../../../../../models/customer";
import InviteCustomerModalSkeleton from "./skeleton";
import useStyles from "./styles";

export interface InviteCustomerModalHandles {
  open: (customers: CustomerIds) => void;
  close: () => void;
}
interface InviteCustomerModalProps {
  onSuccess?: () => void;
}

const InviteCustomerModal: ForwardRefRenderFunction<
  InviteCustomerModalHandles,
  InviteCustomerModalProps
> = (props, ref) => {
  const { onSuccess } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { getCustomersById, useInviteCustomers } = useActionCustomer();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [customerIds, setCustomerIds] = useState<CustomerIds>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [requestsValid, setRequestsValid] = useState<number>(0);

  const haveManyCustomers = customers.length > 1;

  const openModal = useCallback((receivedCustomerIds: CustomerIds) => {
    setCustomerIds(receivedCustomerIds);
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setCustomerIds([]);
    setCustomers([]);
    setModalOpened(false);
    setRequestsValid(0);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const { isFetching: loadingCustomersData } = getCustomersById(customerIds, setCustomers);
  const inviteActions = useInviteCustomers(customerIds, Number(currentUser?.companyId), () =>
    setRequestsValid((prev) => prev + 1)
  );

  const allLoadings = loadingCustomersData || inviteActions.some((invite) => invite.isFetching);

  useEffect(() => {
    if (requestsValid > 0 && requestsValid === customers.length) {
      closeModal();
      if (onSuccess) onSuccess();
    }
  }, [requestsValid, customers]);

  const onSubmitInvite = () => {
    inviteActions.map((invite) => invite.refetch());
  };

  const renderContentByCustomersToRequest = () => {
    if (loadingCustomersData) {
      return <InviteCustomerModalSkeleton />;
    }
    if (!customers.length) return null;

    if (haveManyCustomers) {
      return (
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.customerName}>
              <span data-cy="invite-customer-name">
                {`${translate("doYouWantToInvite")} ${customers.length} ${translate(
                  "customers"
                )} ${translate("toShipeezi")}`}
              </span>
            </div>
          </Grid>
          <Grid item>
            <div className={classes.customerEmail}>
              <span data-cy="invite-customer-email">{`${translate(
                "anInvitationWillBeSendTo"
              )} ${translate("customers")} ${translate("emails")}`}</span>
            </div>
          </Grid>
        </Grid>
      );
    }

    const customer = customers[0];

    return (
      <Grid container spacing={2}>
        <Grid item>
          <div className={classes.customerName}>
            {`${translate("doYouWantToInvite")} `}
            <span data-cy="invite-customer-name">{`${customer?.name}`}</span>
            {` ${translate("toShipeezi")}`}
          </div>
        </Grid>
        <Grid item>
          <div className={classes.customerEmail}>
            {`${translate("anInvitationWillBeSendTo")} `}
            <span data-cy="invite-customer-email">{`${customer?.email}`}</span>
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <ConfirmationModal
      width="520px !important"
      open={modalOpened}
      closeModal={closeModal}
      type="success"
      title={translate("inviteCustomer")}
      leftIconTitle={<VscInfo color="#12B76A" size={20} />}
      loading={allLoadings}
      textSubmit={translate("inviteCustomer")}
      submit={onSubmitInvite}
    >
      <div className={classes.container} data-cy="invite-customer-container">
        {renderContentByCustomersToRequest()}
      </div>
    </ConfirmationModal>
  );
};

export default forwardRef(InviteCustomerModal);
